<template>
  <NavbarTopMobile
    :label="left_label"
    :avatar_src="left_avatar_src"
    avatar_format="square"
    data_test_label="employee-mobile-project"
    @open-sidebar="is_menu_open = true"
  >
    <template #pill>
      <a
        v-if="active_pill && active_pill.is_dropdown"
        class="flex min-h-11 min-w-0 cursor-pointer items-center gap-1"
        :data-test="active_pill.data_test"
        @click="togglePillMenu"
      >
        <span class="truncate text-[16px] font-semibold text-neutral-900">
          {{ active_pill.label }}
        </span>
        <FontAwesomeIcon
          :icon="['far', 'chevron-down']"
          class="shrink-0 text-neutral-500"
        />
      </a>
      <span
        v-else-if="active_pill"
        class="truncate text-base font-semibold text-gray-800"
        :data-test="active_pill.data_test"
      >
        {{ active_pill.label }}
      </span>
    </template>

    <template #menus>
      <PMenu
        ref="sub_tab_menu_ref"
        :model="sub_tab_items"
        :popup="true"
      />

      <PMenu
        ref="feature_menu_ref"
        :model="navbar_items"
        :popup="true"
      >
        <template #item="{ item, props }">
          <NavbarItemHorizontal
            :item="item"
            :params="params"
            v-bind="props.action"
          />
        </template>
      </PMenu>
    </template>
  </NavbarTopMobile>
  <Sidebar v-model="is_menu_open" />
</template>

<script setup>
import { computed, getCurrentInstance, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import Sidebar from '@/app/_employees/components/navbar/TheSidebarEmployee.vue';
import NavbarItemHorizontal from '@/app/shared_components/navbar/NavbarItemHorizontal.vue';
import NavbarTopMobile from '@/app/shared_components/navbar/NavbarTopMobile.vue';
import {
  NAVBAR_ITEMS_EMPLOYEES_OVERVIEW_NAVBAR,
  NAVBAR_ITEMS_EMPLOYEES_SECONDARY_NAVBAR,
} from '@/app/data/navbar_constants';
import { PROFILE_TYPES } from '@/app/data/model_constants';
import { ROUTES_EMPLOYEES } from '@/app/data/route_constants';
import { FT } from '@/app/data/flipper_constants';
import { mapGetters } from '@/store/lib/vue3helpers';

const SETTINGS_TABS = [
  { name: ROUTES_EMPLOYEES.CLIENT_COMPANY, label: 'client.labels.tab_company' },
  { name: ROUTES_EMPLOYEES.SETTINGS_TEAM, label: 'navbar.topnavbar.team' },
  { name: ROUTES_EMPLOYEES.CLIENT_SETTINGS, label: 'client.labels.tab_settings' },
  { name: ROUTES_EMPLOYEES.CLIENT_SUBSCRIPTION, label: 'client.labels.tab_subscription' },
  { name: ROUTES_EMPLOYEES.CLIENT_INTEGRATIONS, label: 'client.labels.tab_integrations' },
];

const { proxy: app } = getCurrentInstance();
const route = useRoute();
const router = useRouter();
const { t } = useI18n();

const { current_client, current_project } = mapGetters(['current_client', 'current_project']);

const is_menu_open = ref(false);
const feature_menu_ref = ref(null);
const sub_tab_menu_ref = ref(null);

const left_label = computed(() => {
  if (current_project.value && current_project.value.attributes && current_project.value.attributes.name) {
    return current_project.value.attributes.name;
  }
  if (current_client.value && current_client.value.attributes && current_client.value.attributes.name) {
    return current_client.value.attributes.name;
  }
  return '';
});

const left_avatar_src = computed(() => {
  if (
    current_project.value &&
    current_project.value.attributes &&
    current_project.value.attributes.picture_login_url_s
  ) {
    return current_project.value.attributes.picture_login_url_s;
  }
  if (current_client.value && current_client.value.attributes) {
    return current_client.value.attributes.picture_logo_url_s;
  }
  return undefined;
});

const isFlipperEnabled = (item) => !item.flipper || app.$flipper.enabled(item.flipper);

const navbar_items = computed(() => {
  if (current_project.value) {
    return NAVBAR_ITEMS_EMPLOYEES_SECONDARY_NAVBAR.filter(
      (item) =>
        isFlipperEnabled(item) && (!current_project.value.attributes.skip_customer || !item.hidden_skip_customer),
    );
  }
  return NAVBAR_ITEMS_EMPLOYEES_OVERVIEW_NAVBAR.filter(isFlipperEnabled);
});

const active_feature = computed(() =>
  navbar_items.value.find((item) => route.name && route.name.startsWith(item.prefix)),
);

const contacts_tabs = computed(() => {
  const tabs = [PROFILE_TYPES.CUSTOMER];
  if (app.$flipper.enabled(FT.SALES)) tabs.push(PROFILE_TYPES.LEAD);
  if (app.$flipper.enabled(FT.DECISIONS_PARTNERS) || app.$flipper.enabled(FT.PROJECT_PARTNERS)) {
    tabs.push(PROFILE_TYPES.PARTNER);
  }
  return tabs;
});

const sub_tabs = computed(() => {
  if (route.name === ROUTES_EMPLOYEES.CLIENT_CONTACTS_TYPE) {
    const active_key = route.params.profile_type || PROFILE_TYPES.CUSTOMER;
    return {
      label: t(`contact.labels.${active_key.toLowerCase()}`),
      items: contacts_tabs.value.map((key) => ({
        label: t(`contact.labels.${key.toLowerCase()}`),
        command: () => router.push({ name: ROUTES_EMPLOYEES.CLIENT_CONTACTS_TYPE, params: { profile_type: key } }),
      })),
    };
  }
  const active_settings_tab = SETTINGS_TABS.find((tab) => route.name && route.name.startsWith(tab.name));
  if (active_settings_tab) {
    return {
      label: t(active_settings_tab.label),
      items: SETTINGS_TABS.map((tab) => ({
        label: t(tab.label),
        command: () => router.push({ name: tab.name }),
      })),
    };
  }
  return null;
});

const TOP_LEVEL_FEATURES = [
  {
    prefix: ROUTES_EMPLOYEES.CLIENT_DASHBOARD,
    title: 'navbar.employee.dashboard',
    data_test: 'employee-mobile-dashboard',
  },
  {
    prefix: ROUTES_EMPLOYEES.SETTINGS_PROFILE,
    title: 'navbar.employee.profile',
    data_test: 'employee-mobile-profile',
  },
];

const active_top_level_feature = computed(() =>
  TOP_LEVEL_FEATURES.find((item) => route.name && route.name.startsWith(item.prefix)),
);

const active_pill = computed(() => {
  if (sub_tabs.value) {
    return { label: sub_tabs.value.label, data_test: 'employee-mobile-sub-tab', is_sub_tab: true, is_dropdown: true };
  }
  if (active_feature.value) {
    return {
      label: t(active_feature.value.title),
      data_test: 'employee-mobile-feature',
      is_sub_tab: false,
      is_dropdown: true,
    };
  }
  if (active_top_level_feature.value) {
    return {
      label: t(active_top_level_feature.value.title),
      data_test: active_top_level_feature.value.data_test,
      is_sub_tab: false,
      is_dropdown: false,
    };
  }
  return null;
});

const sub_tab_items = computed(() => (sub_tabs.value ? sub_tabs.value.items : []));

const params = computed(() => {
  if (!current_project.value) return {};
  return {
    project_id: current_project.value.id,
    level_type: current_project.value.type,
    level_id: current_project.value.id,
  };
});

const togglePillMenu = (event) => {
  const target = active_pill.value && active_pill.value.is_sub_tab ? sub_tab_menu_ref.value : feature_menu_ref.value;
  if (target) target.toggle(event);
};
</script>
